import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter } from 'rxjs/internal/operators/filter';
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Router } from '@angular/router';
import { SnackbarService } from '../snackbar/snackbar.service';
import { environment } from '../../../environments/environment';
import { CONSTANTS_DETAILS } from '../../shared/models/constants';
import { RolesPrivilege } from '../../shared/models/roles-interface';
import {
  BehaviorSubject,
  catchError,
  lastValueFrom,
  map,
  Observable,
  throwError,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { AesEncryptDecryptService } from '../decrypt/aes-encrypt-decrypt-service.service';
import { LicenseManager } from 'ag-grid-enterprise';
import { ApproverAdmin } from '../../shared/models/users';
import { AppTranslateLoaderFactoryService } from '../translate';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public readonly _destroying$ = new Subject<void>();
  public userRoles = new BehaviorSubject<RolesPrivilege>({} as RolesPrivilege);
  public hasRoles = true;

  //Add observable to notify when grid key is loaded
  private gridKeyLoadedSubject = new BehaviorSubject<boolean>(false);
  public gridKeyLoaded$ = this.gridKeyLoadedSubject.asObservable();

  constructor(
    private _authService: MsalService,
    private _msalBroadcastService: MsalBroadcastService,
    private _router: Router,
    private _snackbarService: SnackbarService,
    private _http: HttpClient,
    private _translate: TranslateService,
    private _translateLoader: AppTranslateLoaderFactoryService,
    private _aesEncryptDecryptService: AesEncryptDecryptService,
  ) {}
  /**
   * Initializes the authentication process by enabling account storage events
   *
   * @memberof AuthService
   */

  public authInitiator(): void {
    this._authService.instance.enableAccountStorageEvents();
    // Subscribe to the MSAL broadcast service to listen for account changes
    // This will handle events like account added, removed, login success, and login failure
    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGIN_FAILURE,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          this._router.navigate(['/']);
        }
      });
    // Subscribe to the broadcast service to listen for authentication status changes
    this._msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.isAuthenticated();
        this.getAndUpdateCurrentUserAccount();
      });
  }

  /**
   * Checks if the user is authenticated.
   *
   * @returns {boolean} True if the user is authenticated, otherwise false.
   * @memberof AuthService
   */
  public isAuthenticated(): boolean {
    return this._authService.instance.getAllAccounts().length > 0;
  }

  /**
   * Gets the set current user account information.
   *
   * @returns {void} The current user account information or null if not authenticated.
   * @memberof AuthService
   */
  public async getAndUpdateCurrentUserAccount(): Promise<void> {
    if (this.isAuthenticated()) {
      const activeAccount = this._authService.instance.getActiveAccount();
      if (
        !activeAccount &&
        this._authService.instance.getAllAccounts().length > 0
      ) {
        const accounts = this._authService.instance.getAllAccounts();
        this._authService.instance.setActiveAccount(accounts[0]);
      }
      this.getRolesPrivileges();
      this.getGridKey();
    } else {
      this.loginUser();
    }
  }
  /**
   * Logs in the user using a redirect method.
   *
   * @return {*}  {void}
   * @memberof AuthService
   */
  public loginUser(): void {
    this._authService.loginRedirect({
      scopes: environment.AUTH_CONFIG.scope,
    });
  }

  /**
   * Logs out the user and redirects to the post-logout URI.
   *
   * @memberof AuthService
   */
  public logoutUser(): void {
    this.clearCache();
    this._authService.logoutRedirect({
      postLogoutRedirectUri: environment.AUTH_CONFIG.postLogoutRedirectUri,
      account: this._authService.instance.getActiveAccount(),
    });
  }
  /**
   * ✅ Clear cached state on logout
   */
  public clearCache(): void {
    this.gridKeyLoadedSubject.next(false);
    this.userRoles.next({} as RolesPrivilege);
  }

  /**
   * Gets the user's email address from the current account.
   *
   * @return {*}  {string[]}
   * @memberof AuthService
   */
  public getAccountGroups() {
    return this._authService.instance.getActiveAccount()
      ? this._authService.instance.getActiveAccount()
      : null;
  }

  /**
   * Fetches the roles and privileges of the user from the server.
   *
   * @memberof AuthService
   */
  public async getRolesPrivileges(): Promise<RolesPrivilege | []> {
    return lastValueFrom(
      this._http.get<RolesPrivilege>(
        environment.APP_BASE_URL + CONSTANTS_DETAILS.GET_USERPRIVILEGES_URL,
      ),
    )
      .then((response: RolesPrivilege) => {
        if (response && response.roles && response.roles.length) {
          this.userRoles.next(response || []);
        } else {
          this.hasRoles = false;
          this._router.navigate(['/unauthorized']);
        }
        return response || [];
      })
      .catch((error) => {
        this._snackbarService.open({
          message:
            error && error.message
              ? error.message
              : this._translate.instant('error_in_privilege'),
          type: 'error',
        });
        return [];
      });
  }
  /**
   * Gets the grid key.
   *
   * @return {*}  {Promise<string>}
   * @memberof RecipeService
   */
  public async getGridKey(): Promise<string> {
    return lastValueFrom(
      this._http.get<string>(
        environment.APP_BASE_URL + CONSTANTS_DETAILS.GET_AG_GRID_KEY,
        { responseType: 'text' as 'json' },
      ),
    )
      .then(async (gridKey) => {
        const decryptedKey =
          await this._aesEncryptDecryptService.decryptAES(gridKey);
        LicenseManager.setLicenseKey(decryptedKey);
        this.gridKeyLoadedSubject.next(true);
        return decryptedKey;
      })
      .catch(() => {
        this.gridKeyLoadedSubject.next(false);
        this._snackbarService.open({
          message: this._translate.instant('error_in_grid_key'),
          type: 'error',
        });
        return '';
      });
  }

  /**
   * Fetches data for the people picker component.
   *
   * @memberof AuthService
   */

  public getPeoplePickerData(): Observable<ApproverAdmin[]> {
    return this._http
      .get<{
        userDetails: { email: string }[];
      }>(
        `${environment.APP_BASE_URL}${CONSTANTS_DETAILS.GET_SUPER_ADMIN_USERS}`,
      )
      .pipe(
        map((res: { userDetails: { email: string }[] }) => {
          const userDetails =
            res && Array.isArray(res.userDetails) ? res.userDetails : [];
          if (!userDetails.length) {
            throw new Error('Failed to fetch user list');
          }
          return userDetails.map(({ email }: { email: string }) => ({
            name: email?.split('@')[0],
            id: email,
          }));
        }),
        catchError((err: unknown) => {
          return throwError(() => err);
        }),
      );
  }
}
